/* eslint-disable react/jsx-no-comment-textnodes */
/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  FormHelperText,
  FormLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import FormControl, { useFormControl } from "@mui/material/FormControl";
import { useNavigate } from "react-router-dom";
import { useStores } from "@/app/csr-src/models";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import cn from "clsx";
import { UKDialog, UKImg } from "@/common-components-src/js/main";
import TipIcon from "@/app/csr-src/assets/icons/TipIcon.svg";
import Closed from "@/app/csr-src/assets/icons/Closed.svg";
import VisaIcon from "@/app/csr-src/assets/icons/visa.svg";
import MasterCardIcon from "@/app/csr-src/assets/icons/MasterCardIcon.svg";
import DefaultCard from "@/app/csr-src/assets/icons/DefaultCard.svg";
import Styles from "./index.module.scss";
import { BNP_BANK_STR } from "@/app/csr-src/apis/checkout/constants/constants";

interface CreditCardFormProps { }

const CreditCardForm: FC<CreditCardFormProps> = observer(() => {
  const { t } = useTranslation();

  const { checkoutStore } = useStores();
  const {
    creditCardForm,
    setCreditCardForm,
    creditCardValid,
    bnpCreditCardSubmitForm,
    handleBlur,
    handleFocus,
    creditBankType,
  } = checkoutStore.creditCardStore;

  const [applyCode, updateApplyCode] = useState < string | undefined > (undefined);
  const [cvvDialogVisible, setCvvDialogVisible] = useState(false);
  const inputRefs: any = {};
  const formFields = [
    {
      prop: "cardNo",
      type: "text",
    },
    {
      prop: "expireDateMonth",
      type: "select",
    },
    {
      prop: "expireDateYear",
      type: "select",
    },
    {
      prop: "cvv",
      type: "text",
    },
  ];

  // 初始化inputRefs
  formFields.forEach((field: any) => {
    inputRefs[field] = useRef(null);
  });

  // 处理表单-textfield数据变化
  const handleFormChange = (e: any, field: string) => {
    let value = e.target.value;
    // 替换空格
    value = value.replace(/\s/g, "");

    // 处理输入
    if (isNaN(Number(value))) {
      return;
    }

    switch (field) {
      case "cardNo":
        if (value.length > 19) {
          value = value.slice(0, 19);
        }
        // 每4个字符添加一个空格
        value = value.replace(/(\d{4})(?=\d)/g, "$1 ");
        break;
      case "cvv":
        value = value.replace(/\//g, "");
        if (value.length > 4) {
          value = value.slice(0, 4);
        }
        break;
      default:
        break;
    }
    setCreditCardForm({ ...(creditCardForm as any), [field]: value });
  };

  // 处理表单- select数据变化
  const handleSelectChanges = (e: any, field: string) => {
    const value = e.target.value;
    setCreditCardForm({ ...(creditCardForm as any), [field]: value });
  };

  // 处理input输入事件
  const handleKeyDown = (e: any, field: string) => {
    // 8-退格、9-tab、13-回车、37-左、38-上、39-右、40-下、46-删除
    const keyCode = [8, 9, 13, 37, 38, 39, 40, 46];
    // 67-复制、86-粘贴、88-剪切
    const copyKeys = [67, 86, 88];
    const charCode = e.which || e.keyCode;
    const keyChar = String.fromCharCode(charCode);

    if (e.keyCode === 13) {
      const inputRef = inputRefs[field];
      inputRef.current.blur();
      return;
    }

    // 判断
    if (
      !/[0-9]/.test(keyChar) &&
      charCode > 0 &&
      !(
        keyCode.includes(charCode) ||
        (e.ctrlKey && copyKeys.includes(charCode)) || // Ctrl+C（复制）、Ctrl+V（粘贴）、Ctrl+X（剪贴）
        (e.metaKey && copyKeys.includes(charCode)) || // Command+C（Mac复制）、Command+V（Mac粘贴）、Command+X（Mac剪贴）
        ((e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) && charCode === 65) // Command+A（Mac全选）或Ctrl+Shift+A（部分软件中的全选快捷键）
      )
    ) {
      e.preventDefault();
    }
  };

  const setMonths = () => {
    const months = [];
    for (let i = 1; i <= 12; i++) {
      months.push(i < 10 ? `0${i}` : `${i}`);
    }
    return months;
  };

  const setYears = () => {
    const years = [];
    for (let i = 0; i < 70; i++) {
      years.push(new Date().getFullYear() + i + "");
    }
    return years;
  };

  const PrexCardIcon = () => {
    return (
      {
        [BNP_BANK_STR.VISA]: <VisaIcon />,
        [BNP_BANK_STR.MASTER_CARD]: <MasterCardIcon />,
        [BNP_BANK_STR.DEFAULT]: <DefaultCard />,
      } as any
    )[creditBankType];
  };

  return (
    <>
      <Box
        component="form"
        sx={{
          minWidth: 120,
          ".MuiOutlinedInput-root": { height: "40px" },
          ".Mui-error": { marginLeft: "0px" },
          ".MuiFormHelperText-root": {
            marginLeft: "0px",
            color: "#FF0F23",
          },
          ".MuiFormHelperText-root.Mui-error": {
            color: "#FF0F23",
          },
        }}
      >
        <FormControl className={Styles.cardFormBox}>
          <div className={Styles.cardFormItem}>
            <FormLabel className={Styles.cardFormItem__label}>
              {t("checkout.creditCard.label.cardNo")}
            </FormLabel>
            <TextField
              sx={{
                width: "100%",
              }}
              inputRef={inputRefs["cardNo"]}
              id="outlined-controlled"
              placeholder={t("checkout.creditCard.placeholder.cardNo")}
              fullWidth
              size="small"
              required
              value={creditCardForm["cardNo"]}
              error={!!creditCardValid["cardNo"]}
              helperText={
                creditCardValid["cardNo"] &&
                t(`checkout.creditCard.validate.cardNo.${creditCardValid["cardNo"]}`)
              }
              onChange={(e) => {
                handleFormChange(e, "cardNo");
              }}
              onKeyDown={(e) => handleKeyDown(e, "cardNo")}
              onBlur={(e) => handleBlur(e, "cardNo")}
              onFocus={(e) => handleFocus(e, "cardNo")}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">{PrexCardIcon()}</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {creditCardForm["cardNo"] && (
                        <IconButton
                          onClick={() => {
                            handleFormChange({ target: { value: "" } }, "cardNo");
                            handleBlur(null, "cardNo");
                          }}
                        >
                          <Closed width={20} style={{ cursor: "pointer" }} />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>

          <div className={Styles.timeCvvBox}>
            <div className={cn(Styles.cardFormItem)}>
              <FormLabel className={Styles.cardFormItem__label}>
                {t("checkout.creditCard.label.expireDate")}
              </FormLabel>
              <div className={Styles.cardFormSelect}>
                <FormControl className={Styles.selectBox}>
                  <Select
                    sx={{ width: "100%" }}
                    inputRef={inputRefs["expireDateMonth"]}
                    displayEmpty
                    name="month"
                    value={creditCardForm["expireDateMonth"]}
                    error={!!creditCardValid["expireDateMonth"]}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <div className={Styles.placeholder}>
                            {t("checkout.creditCard.placeholder.expireDateMonth")}
                          </div>
                        );
                      }
                      return selected;
                    }}
                    onChange={(e) => {
                      handleSelectChanges(e, "expireDateMonth");
                    }}
                    onClose={(e) => handleBlur(e, "expireDateMonth")}
                  >
                    {setMonths().map((item: any, index: number) => {
                      return (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl className={Styles.selectBox}>
                  <Select
                    sx={{ width: "100%" }}
                    displayEmpty
                    name="year"
                    value={creditCardForm["expireDateYear"]}
                    error={!!creditCardValid["expireDateYear"]}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <div className={Styles.placeholder}>
                            {t("checkout.creditCard.placeholder.expireDateYear")}
                          </div>
                        );
                      }
                      return selected;
                    }}
                    onChange={(e) => {
                      handleSelectChanges(e, "expireDateYear");
                    }}
                    onClose={(e) => handleBlur(e, "expireDateYear")}
                  >
                    {setYears().map((item: any, index: number) => {
                      return (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
              <FormHelperText>
                {(creditCardValid["expireDateMonth"] &&
                  t(
                    `checkout.creditCard.validate.expireDateMonth.${creditCardValid["expireDateMonth"]}`,
                  )) ||
                  (creditCardValid["expireDateYear"] &&
                    t(
                      `checkout.creditCard.validate.expireDateYear.${creditCardValid["expireDateYear"]}`,
                    ))}
              </FormHelperText>
            </div>
            <div className={Styles.cardFormItem}>
              <FormLabel className={Styles.cardFormItem__label}>
                {t("checkout.creditCard.label.cvv")}
                <TipIcon
                  width={20}
                  style={{ marginTop: "2px", marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => setCvvDialogVisible(true)}
                />
              </FormLabel>
              <TextField
                sx={{
                  width: "100%",
                }}
                inputRef={inputRefs["cvv"]}
                id="outlined-controlled"
                // label={t("checkout.creditCard.label.cvv")}
                placeholder={t("checkout.creditCard.placeholder.cvv")}
                fullWidth
                size="small"
                required
                value={creditCardForm["cvv"]}
                error={!!creditCardValid["cvv"]}
                helperText={
                  creditCardValid["cvv"] &&
                  t(`checkout.creditCard.validate.cvv.${creditCardValid["cvv"]}`)
                }
                onChange={(e) => {
                  handleFormChange(e, "cvv");
                }}
                onKeyDown={(e) => handleKeyDown(e, "cvv")}
                onBlur={(e) => handleBlur(e, "cvv")}
                onFocus={(e) => handleFocus(e, "cvv")}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {creditCardForm["cvv"] && (
                          <IconButton
                            onClick={() => {
                              handleFormChange({ target: { value: "" } }, "cvv");
                              handleBlur(null, "cvv");
                            }}
                          >
                            <Closed width={20} style={{ cursor: "pointer" }} />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>
          </div>
        </FormControl>
        <UKDialog
          open={cvvDialogVisible}
          onClose={() => setCvvDialogVisible(false)}
          showCloseBtn={true}
        >
          <DialogTitle>{t("checkout.creditCard.modal.cvv.title")}</DialogTitle>

          {/* Error info */}
          <DialogContent className={Styles.cvvDialogContent}>
            <UKImg
              url="https://st.joy-sourcing.com/website/other/ME1733902493688.png"
              externalStyles={{ width: "441px", height: "216px" }}
              externalMaskStyles={{ background: "rgba(0, 0, 0, 0)" }}
            />
          </DialogContent>

          {/* Button */}
          <DialogActions>
            <Button onClick={() => setCvvDialogVisible(false)} size="small" variant="outlined">
              {t("common.btnText.ok")}
            </Button>
          </DialogActions>
        </UKDialog>
      </Box>
      <div>
        <form
          name="card form"
          action={bnpCreditCardSubmitForm.actionUrl}
          method="post"
          id="bnpSubmitForm"
        >
          <input type="hidden" name="brand" value={creditBankType} />
          <input type="hidden" name="MerchantID" value={bnpCreditCardSubmitForm.MerchantID} />
          <input type="hidden" name="Len" value={bnpCreditCardSubmitForm.Len} />
          <input type="hidden" name="Data" value={bnpCreditCardSubmitForm.Data} />
          <input type="hidden" name="cardholder" value={bnpCreditCardSubmitForm.cardHolderName} />
          
          <input
            type="hidden"
            name="number"
            value={bnpCreditCardSubmitForm.number.replace(/\s/g, "")}
          />
          <input type="hidden" name="expiryDate" value={bnpCreditCardSubmitForm.expiryDate} />
          <input type="hidden" name="securityCode" value={bnpCreditCardSubmitForm.securityCode} />
        </form>
      </div>
    </>
  );
});

export default CreditCardForm;
